import React, { useState, useEffect } from 'react';
import { Card, Select, Alert, Typography, Row, Col, Tabs, Skeleton } from 'antd';
import { TrendLineChart } from './TrendLineChart';
import { ANALYTICS_API_URL } from '../../../../constants/config';
import { getAndSanitize } from '../../../../shared/utils/sanitization';


const { Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

interface TrendDataPoint {
  date: string;
  value: number;
  percentage?: number;
}

interface RejectedAmountTrendData {
  daily: TrendDataPoint[];
  weekly: TrendDataPoint[];
  monthly: TrendDataPoint[];
  totalRejectedAmount: TrendDataPoint[];
}

interface RejectedAmountTrendChartProps {
  dateRange?: [string, string] | null;
  adminId?: string;
  loading?: boolean;
}

export const RejectedAmountTrendChart: React.FC<RejectedAmountTrendChartProps> = ({
  dateRange,
  adminId,
  loading: propLoading = false
}) => {
  const [data, setData] = useState<RejectedAmountTrendData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'count' | 'amount'>('count');
  const [activePeriod, setActivePeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');

  const fetchRejectedAmountTrend = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getAndSanitize("token");
      if (!token) {
        throw new Error("Token no disponible");
      }

      const params = new URLSearchParams();
      if (dateRange) {
        // Usar directamente las fechas ISO del filtro de Analytics
        params.append("dateFrom", dateRange[0]);
        params.append("dateTo", dateRange[1]);
      }
      if (adminId) {
        params.append("adminId", adminId);
      }

      const response = await fetch(
        `${ANALYTICS_API_URL}/api/analytics/parse-analytics/rejected-amount-trend?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setData(result.data);
      } else {
        console.error("RejectedAmountTrendChart - API Error:", result);
        throw new Error(result.message || "Error al obtener datos de tendencia");
      }
    } catch (err: any) {
      console.error("Error fetching rejected amount trend:", err);
      setError(err.message || "Error al cargar tendencia de montos rechazados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRejectedAmountTrend();
  }, [dateRange, adminId]);

  const prepareChartData = () => {
    if (!data) return [];

    if (activeView === 'count') {
      const chartData = data[activePeriod].map(point => ({
        date: point.date,
        value: point.value,
        type: 'Rechazos'
      }));
      return chartData;
    } else {
      const chartData = data.totalRejectedAmount.map(point => ({
        date: point.date,
        value: point.value,
        type: 'Monto ($)'
      }));
      return chartData;
    }
  };

  const hasData = () => {
    return data && (
      data.daily.length > 0 || 
      data.weekly.length > 0 || 
      data.monthly.length > 0 || 
      data.totalRejectedAmount.length > 0
    );
  };

  const calculateTotalStats = () => {
    if (!data) return { totalCount: 0, totalAmount: 0, avgDaily: 0 };

    const totalCount = data.daily.reduce((sum, item) => sum + item.value, 0);
    const totalAmount = data.totalRejectedAmount.reduce((sum, item) => sum + item.value, 0);
    const avgDaily = data.daily.length > 0 ? totalCount / data.daily.length : 0;

    return { totalCount, totalAmount, avgDaily };
  };

  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (error) {
    return (
      <Card title="Tendencia de Montos Rechazados">
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  const stats = calculateTotalStats();

  return (
    <Card
      title="Tendencia de Montos Rechazados"
      extra={
        <Select
          value={activeView}
          onChange={setActiveView}
          style={{ width: 160 }}
        >
          <Option value="count">Cantidad de Rechazos</Option>
          <Option value="amount">Monto Total</Option>
        </Select>
      }
    >
      {(loading || propLoading) ? (
        <div style={{ padding: '16px' }}>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Skeleton active paragraph={{ rows: 1 }} />
            </Col>
            <Col span={8}>
              <Skeleton active paragraph={{ rows: 1 }} />
            </Col>
            <Col span={8}>
              <Skeleton active paragraph={{ rows: 1 }} />
            </Col>
          </Row>
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      ) : hasData() ? (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <Title level={4} style={{ margin: 0, color: '#ff4d4f' }}>
                  {stats.totalCount}
                </Title>
                <Typography.Text type="secondary">Total Rechazos</Typography.Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <Title level={4} style={{ margin: 0, color: '#fa8c16' }}>
                  {formatAmount(stats.totalAmount)}
                </Title>
                <Typography.Text type="secondary">Monto Total Rechazado</Typography.Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                  {Math.round(stats.avgDaily * 10) / 10}
                </Title>
                <Typography.Text type="secondary">Promedio Diario</Typography.Text>
              </div>
            </Col>
          </Row>

          <Tabs activeKey={activeView === 'count' ? activePeriod : 'amount'} onChange={(key) => {
            if (key === 'amount') {
              setActiveView('amount');
            } else {
              setActiveView('count');
              setActivePeriod(key as 'daily' | 'weekly' | 'monthly');
            }
          }}>
            <TabPane tab="Diario" key="daily">
              <TrendLineChart
                data={prepareChartData()}
                xField="date"
                yField="value"
                height={400}
                smooth={true}
              />
            </TabPane>
            <TabPane tab="Semanal" key="weekly">
              <TrendLineChart
                data={prepareChartData()}
                xField="date"
                yField="value"
                height={400}
                smooth={true}
              />
            </TabPane>
            <TabPane tab="Mensual" key="monthly">
              <TrendLineChart
                data={prepareChartData()}
                xField="date"
                yField="value"
                height={400}
                smooth={true}
              />
            </TabPane>
            <TabPane tab="Monto Total" key="amount">
              <TrendLineChart
                data={prepareChartData()}
                xField="date"
                yField="value"
                height={400}
                smooth={true}
              />
            </TabPane>
          </Tabs>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Typography.Text type="secondary">No hay datos disponibles</Typography.Text>
        </div>
      )}
    </Card>
  );
}; 